import React, { useState } from 'react';

import { DeleteOutline, ErrorOutline, HourglassTop, Update } from '@mui/icons-material';
import { MenuItem, Stack, TextField } from '@mui/material';
import { DataGrid, GridCellParams, GridColumnVisibilityModel } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';

import { DEFAULT_PAGE_SIZES } from '~/consts/filters';

import { PromoMeta, PromoSortBy, PromoSortDirection, isPromoSortBy, promoSortOptions } from '~/services/PromoService';

import { BulkOperation, BulkOperationRowStatus } from '../PromoBulkOperations';
import ColumnSelector from '../PromoTester/ColumnSelector';

import getColumns, { getRefinedColumns } from './columns';

interface Props {
  expirePromo: (id: string) => Promise<void>;
  onPageChange: (page: number, pageSize: number) => void;
  onClick: (rowPromo: GridCellParams<App.PromoData>) => void;
  page: number;
  promos: Array<App.PromoData>;
  sizePerPage: number;
  total: number;
  isLoading: boolean;
  promoMeta: PromoMeta;
  isDev: boolean;
  bulkOperation?: BulkOperation | null;
  bulkRowStatuses?: Record<string, BulkOperationRowStatus>;
  refined?: boolean;
  hideParentPromoId?: boolean;
  sortBy?: PromoSortBy;
  sortDirection?: PromoSortDirection;
  onSortChange?: (sortBy: PromoSortBy, sortDirection: PromoSortDirection) => void;
}

function PromoPage({
  promos,
  total,
  page,
  onPageChange,
  onClick,
  expirePromo,
  sizePerPage,
  isLoading,
  promoMeta,
  isDev,
  bulkOperation = null,
  bulkRowStatuses = {},
  refined = false,
  hideParentPromoId = false,
  sortBy = 'created_at',
  sortDirection = 'desc',
  onSortChange,
}: Props) {
  const data = promos.map((promo) => ({
    ...promo,
    actions: { expirePromo },
  }));

  const [columnVisibility, setColumnVisibility] = useState<GridColumnVisibilityModel>({});
  const columnVisibilityModel = {
    popup_message_presented_example: isDev,
    cloned_from_promo_id: !hideParentPromoId,
    ...columnVisibility,
  };
  const columns = [
    ...(refined ? getRefinedColumns(promoMeta) : getColumns(promoMeta)),
    ...(bulkOperation
      ? [
          {
            field: 'bulk_operation_status',
            headerName: 'Bulk operation',
            sortable: false,
            width: 150,
            renderCell: (params) => {
              const status = bulkRowStatuses[params.row.id_promo_code];
              if (status === 'pending') {
                return <HourglassTop color="warning" titleAccess="Pending" />;
              }
              if (status === 'deleted') {
                return <DeleteOutline color="success" titleAccess="Deleted" />;
              }
              if (status === 'updated') {
                return <Update color="success" titleAccess="Updated" />;
              }
              if (status === 'failed') {
                return <ErrorOutline color="error" titleAccess="Failed" />;
              }
              return null;
            },
          },
        ]
      : []),
  ].map((column) => ({
    ...column,
    ...(onSortChange ? { sortable: isPromoSortBy(column.field) } : {}),
  }));

  return (
    <Stack spacing={2}>
      {onSortChange && (
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <ColumnSelector
            columns={columns}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityChange={(field, visible) =>
              setColumnVisibility((previous) => ({ ...previous, [field]: visible }))
            }
          />
          <TextField
            select
            size="small"
            label="Sort by"
            value={sortBy}
            onChange={(event) => {
              if (isPromoSortBy(event.target.value)) onSortChange(event.target.value, sortDirection);
            }}
            sx={{ minWidth: 170 }}
          >
            {promoSortOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Sort direction"
            value={sortDirection}
            onChange={(event) => onSortChange(sortBy, event.target.value === 'asc' ? 'asc' : 'desc')}
            sx={{ minWidth: 170 }}
          >
            <MenuItem value="asc">Ascending</MenuItem>
            <MenuItem value="desc">Descending</MenuItem>
          </TextField>
        </Stack>
      )}
      <DataGrid
        columns={columns}
        sortingMode={onSortChange ? 'server' : 'client'}
        sortModel={onSortChange ? [{ field: sortBy, sort: sortDirection }] : undefined}
        sortingOrder={['asc', 'desc']}
        onSortModelChange={(model) => {
          const sort = model[0];
          if (onSortChange && sort && isPromoSortBy(sort.field) && sort.sort) {
            onSortChange(sort.field, sort.sort);
          }
        }}
        rows={data}
        rowCount={total}
        pagination
        paginationMode="server"
        paginationModel={{ page: page - 1, pageSize: sizePerPage }}
        slots={{ pagination: GridPagination }}
        pageSizeOptions={DEFAULT_PAGE_SIZES}
        onPaginationModelChange={({ page, pageSize }) => onPageChange(page, pageSize)}
        loading={isLoading}
        getRowId={(row: App.PromoData) => row.id_promo_code}
        density={refined ? 'compact' : 'standard'}
        rowHeight={refined ? 64 : undefined}
        getRowHeight={refined ? undefined : () => 'auto'}
        sx={{ '.MuiDataGrid-cell': { alignItems: refined ? 'center' : 'start' } }}
        onCellClick={(params: GridCellParams<App.PromoData>) => onClick(params)}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibility}
        disableRowSelectionOnClick
        disableColumnMenu
        autoHeight
      />
    </Stack>
  );
}

export default PromoPage;
